import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DistributionFormValues } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { DISTRIBUTION_SEND_MODE_TO_API, DISTRIBUTION_TYPE_TO_API } from 'constants/promo-code'
import { mapFormToDistributionPayload } from 'modules/promo-code/api-adapters'
import { buildWhitelistFile } from 'modules/promo-code/whitelist/import-file-utils'
import { logError } from 'utils/logger'

import { promoCodeKeys } from './query-keys'

const invalidateDistributions = (
  queryClient: ReturnType<typeof useQueryClient>,
  promoCodeId: string
) => {
  queryClient.invalidateQueries({
    queryKey: [...promoCodeKeys.detail(promoCodeId), 'distributions'],
  })
  // The list's "Whitelist & Email" cell shows the latest setup — refresh it too.
  queryClient.invalidateQueries({ queryKey: promoCodeKeys.lists() })
}

/** Save whitelist (and optionally send email) — one call per Save & Send Email click. */
export const useCreateDistribution = (promoCodeId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: DistributionFormValues) => {
      if (!promoCodeId) throw new Error('Missing promo code id')

      if (values.type === 'IMPORT_FILE') {
        const importFile = values.importFile
        if (!importFile?.emails.length) throw new Error('No emails to import')

        const formData = new FormData()
        formData.append('promoCodeId', promoCodeId)
        formData.append('type', String(DISTRIBUTION_TYPE_TO_API.IMPORT_FILE))
        formData.append('sendEmail', String(values.sendEmail))
        if (values.sendEmail) {
          formData.append(
            'sendMode',
            String(DISTRIBUTION_SEND_MODE_TO_API[values.sendMode ?? 'ALL_WHITELIST'])
          )
          formData.append('subject', values.subject?.trim() ?? '')
          formData.append('body', values.body?.trim() ?? '')
        }
        formData.append('file', buildWhitelistFile(importFile.fileName, importFile.emails))

        const res = await AppAdmin.createPromoCodeDistributionWithFile(formData)
        return res.data?.data
      }

      const res = await AppAdmin.createPromoCodeDistribution(
        mapFormToDistributionPayload(promoCodeId, values)
      )
      return res.data?.data
    },
    onSuccess: () => {
      if (promoCodeId) invalidateDistributions(queryClient, promoCodeId)
    },
    onError: (err) => logError('Create promo code distribution failed', err),
  })
}

/** Resend the distribution's email to its failed recipients. */
export const useResendDistribution = (promoCodeId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (distributionId: string) => {
      const res = await AppAdmin.resendPromoCodeDistribution(distributionId)
      return res.data?.data
    },
    onSuccess: () => {
      if (promoCodeId) invalidateDistributions(queryClient, promoCodeId)
    },
    onError: (err) => logError('Resend distribution email failed', err),
  })
}
